import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSortableTable } from 'packages/utils';
import useGetCorporations from '@src/packages/utils/hooks/workDirection/useGetCorporations';
import { useRulesByCountry } from '@src/packages/utils/hooks/useRulesByCountry';
import { DateUtil } from '@src/packages/utils/dates';
import { Form, notification, Tooltip } from 'antd';
import moment from 'moment';
import {
  downloadActiveEnterprisesAsDocx,
  downloadActiveEnterprisesAsExcel,
  downloadActiveEnterprisesAsPdf,
  printActiveEnterprisesAsPdf,
} from '../utils';
import { extractTextFromJSX } from '@src/util/jsx';
import { useTimeTracker } from '../../../../contexts/TimeTrackerProvider';

// auditUrl is stored with or without the scheme ("https://app.operonda.com/...").
const toAbsoluteUrl = url => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

export const useActiveEnterprises = ({ isActive = true }) => {
  const intl = useIntl();
  const { country } = useTimeTracker();
  const { countryRulesForm } = useRulesByCountry({ countryName: country });
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({
    search: '',
    startDate: undefined,
    endDate: undefined,
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const { data: corporations, loading } = useGetCorporations({
    runFirstTime: true,
    isActive,
  });

  const formatIdCard = rut => countryRulesForm?.identifier?.formatIdentifier(rut);
  const formatDate = date => DateUtil.toDateString(date, DateUtil.DEFAULT_DT_DATE_FORMAT);

  const status = isActive ? 'active' : 'inactive';

  const filteredCorporations = useMemo(() => {
    if (!corporations || corporations.length === 0) return [];

    return corporations
      .map((corporation, index) => ({
        ...corporation,
        key: `${corporation.id}_${index}`,
      }))
      .filter(corporation => {
        const { search, startDate, endDate } = filters;

        if (search) {
          const searchLower = search.toLowerCase();
          const matchesSearch =
            corporation.name?.toLowerCase().includes(searchLower) ||
            corporation.tradeName?.toLowerCase().includes(searchLower) ||
            corporation.RUT?.toLowerCase().includes(searchLower) ||
            corporation.address?.toLowerCase().includes(searchLower) ||
            corporation.serviceType?.toLowerCase().includes(searchLower);

          if (!matchesSearch) return false;
        }

        if (startDate && corporation.contractExpiryDate) {
          const contractDate = moment(corporation.contractExpiryDate);
          if (contractDate.isBefore(moment(startDate, DateUtil.DEFAULT_DATE_FORMAT))) return false;
        }

        if (endDate && corporation.contractExpiryDate) {
          const contractDate = moment(corporation.contractExpiryDate);
          if (contractDate.isAfter(moment(endDate, DateUtil.DEFAULT_DATE_FORMAT))) return false;
        }

        return true;
      });
  }, [corporations, filters]);

  const paginatedCorporations = useMemo(() => {
    const startIndex = (pagination.current - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredCorporations.slice(startIndex, endIndex);
  }, [filteredCorporations, pagination]);

  const handlePaginationChange = (page, pageSize) => {
    setPagination({ current: page, pageSize });
  };

  const handleDownloadExcel = async (columns, data) => {
    try {
      const cleanColumns = columns.map(col => ({
        title: extractTextFromJSX(col.title),
        dataIndex: col.dataIndex,
      }));

      await downloadActiveEnterprisesAsExcel(
        data,
        cleanColumns,
        intl.formatMessage({ id: `timetracker.${status}.enterprises.report` }),
        formatIdCard,
      );

      notification.success({
        message: intl.formatMessage({ id: 'general.download.success' }),
        key: 'download',
      });
    } catch (error) {
      console.error('Error downloading Excel:', error);
      notification.error({
        message: intl.formatMessage({ id: 'general.download.error' }),
        key: 'download',
      });
    }
  };

  const handleDownloadDocx = async (columns, data) => {
    try {
      const cleanColumns = columns.map(col => ({
        title: extractTextFromJSX(col.title),
        dataIndex: col.dataIndex,
      }));

      downloadActiveEnterprisesAsDocx(
        data,
        cleanColumns,
        intl.formatMessage({ id: `timetracker.${status}.enterprises.report` }),
        formatIdCard,
      );

      notification.success({
        message: intl.formatMessage({ id: 'general.download.success' }),
        key: 'download',
      });
    } catch (error) {
      console.error('Error downloading Docx:', error);
      notification.error({
        message: intl.formatMessage({ id: 'general.download.error' }),
        key: 'download',
      });
    }
  };

  const handleDownloadPdf = async (columns, data) => {
    try {
      const cleanColumns = columns.map(col => ({
        title: extractTextFromJSX(col.title),
        dataIndex: col.dataIndex,
      }));

      await downloadActiveEnterprisesAsPdf(
        data,
        cleanColumns,
        intl.formatMessage({ id: `timetracker.${status}.enterprises.report` }),
        formatIdCard,
      );

      notification.success({
        message: intl.formatMessage({ id: 'general.download.success' }),
        key: 'download',
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      notification.error({
        message: intl.formatMessage({ id: 'general.download.error' }),
        key: 'download',
      });
    }
  };

  const handlePrintPdf = async (columns, data) => {
    try {
      const cleanColumns = columns.map(col => ({
        title: extractTextFromJSX(col.title),
        dataIndex: col.dataIndex,
      }));

      await printActiveEnterprisesAsPdf(data, cleanColumns, formatIdCard);
    } catch (error) {
      console.error('Error printing PDF:', error);
      notification.error({
        message: intl.formatMessage({ id: 'general.download.error' }),
        key: 'download',
      });
    }
  };

  const handleSearchChange = e => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handlePeriodChange = value => {
    if (value && typeof value === 'number') {
      const endDate = moment();
      const startDate = moment().subtract(value, 'days');

      form.setFieldsValue({ period: value, startDate: startDate, endDate: endDate });
      setFilters(prev => ({
        ...prev,
        startDate: startDate.format(DateUtil.DEFAULT_DATE_FORMAT),
        endDate: endDate.format(DateUtil.DEFAULT_DATE_FORMAT),
      }));
    } else {
      form.setFieldsValue({ period: value, startDate: undefined, endDate: undefined });
      setFilters(prev => ({ ...prev, startDate: undefined, endDate: undefined }));
    }
  };

  const handleStartDateChange = value => {
    form.setFieldsValue({ startDate: value, period: undefined });
    setFilters(prev => ({
      ...prev,
      startDate: value ? value.format(DateUtil.DEFAULT_DATE_FORMAT) : undefined,
    }));
  };

  const handleEndDateChange = value => {
    form.setFieldsValue({ endDate: value, period: undefined });
    setFilters(prev => ({
      ...prev,
      endDate: value ? value.format(DateUtil.DEFAULT_DATE_FORMAT) : undefined,
    }));
  };

  const { sortedData, createSortableColumn } = useSortableTable(paginatedCorporations);

  const baseColumns = [
    {
      key: 'name',
      title: intl.formatMessage({ id: 'timetracker.active.enterprises.column.businessName' }),
      dataIndex: 'name',
      align: 'left',
      width: '170px',
      render: corporation => <div>{corporation?.name || '-'}</div>,
    },
    {
      key: 'tradeName',
      title: intl.formatMessage({ id: 'timetracker.active.enterprises.column.fantasyName' }),
      dataIndex: 'tradeName',
      align: 'left',
      render: corporation => <div>{corporation?.tradeName || '-'}</div>,
    },
    {
      key: 'address',
      title: intl.formatMessage({ id: 'timetracker.active.enterprises.column.address' }),
      dataIndex: 'address',
      align: 'left',
      render: corporation => (
        <Tooltip title={corporation?.address}>
          <div className="gx-text-truncate" style={{ maxWidth: '180px' }}>
            {corporation?.address || '-'}
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'rut',
      title: intl.formatMessage({ id: 'timetracker.active.enterprises.column.rut' }),
      dataIndex: 'rut',
      align: 'left',
      width: '120px',
      render: corporation => <div>{formatIdCard(corporation?.RUT) || '-'}</div>,
    },
    {
      key: 'serviceType',
      title: intl.formatMessage({ id: 'timetracker.active.enterprises.column.serviceType' }),
      dataIndex: 'serviceType',
      align: 'left',
      render: corporation => <div>{corporation?.serviceType || '-'}</div>,
    },
    {
      key: 'auditUrl',
      title: intl.formatMessage({ id: 'timetracker.active.enterprises.column.auditUrl' }),
      dataIndex: 'auditUrl',
      align: 'left',
      render: corporation => (
        <div>
          {corporation?.auditUrl ? (
            <a href={toAbsoluteUrl(corporation.auditUrl)} target="_blank" rel="noopener noreferrer">
              {corporation.auditUrl}
            </a>
          ) : (
            '-'
          )}
        </div>
      ),
    },
    {
      key: 'contractExpiryDate',
      title: intl.formatMessage({ id: 'timetracker.active.enterprises.column.contractExpiryDate' }),
      dataIndex: 'contractExpiryDate',
      align: 'left',
      render: corporation => (
        <div>
          {corporation?.contractExpiryDate ? formatDate(corporation.contractExpiryDate) : '-'}
        </div>
      ),
    },
  ];

  const columns = baseColumns.map(createSortableColumn);

  return {
    columns,
    data: sortedData,
    loading,
    formatIdCard,
    handleDownloadExcel,
    handleDownloadDocx,
    handleDownloadPdf,
    handlePrintPdf,
    handleSearchChange,
    handlePeriodChange,
    handleStartDateChange,
    handleEndDateChange,
    pagination: {
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: filteredCorporations.length,
      onChange: handlePaginationChange,
      showSizeChanger: true,
      showTotal: (total, range) =>
        intl.formatMessage(
          { id: 'general.pagination.total' },
          { start: range[0], end: range[1], total },
        ),
      pageSizeOptions: ['10', '20', '50', '100'],
    },
    totalFiltered: filteredCorporations.length,
    totalRecords: corporations.length,
    form,
    filters,
    setFilters,
  };
};
